import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface Overview {
  readonly totalSearches: number;
  readonly successfulSearches: number;
  readonly failedSearches: number;
  readonly zeroResultSearches: number;
  readonly successRate: number;
  readonly avgExecutionMs: number;
  readonly p95TargetMs: number;
}
interface TopSearch { readonly query: string; readonly count: number; readonly avgResults: number }
interface FailedSearch { readonly query: string; readonly status: string; readonly count: number }
interface PopularContent { readonly searchIndexId: string; readonly title: string; readonly clicks: number }
interface Adoption { readonly indexedItems: number; readonly activeSearchers: number; readonly searchesPerSearcher: number }
interface Trend { readonly day: string; readonly searches: number; readonly failures: number }

/**
 * M39 — Search Analytics Dashboard (§18). Read-only measurement of search
 * effectiveness, feeding M37 BI. Overview KPIs (volume, success rate vs the
 * governed P95 target), the top queries, the failed / zero-result queries that
 * signal content gaps (BR-04), the most-clicked content, adoption (indexed items
 * vs active searchers) and a daily trend series — all computed live from the
 * T516 query log and T517 result set. Requires ANALYTICS_VIEW; tenant isolation
 * is enforced server-side (LAW 4).
 */
export function SearchAnalyticsDashboard(): ReactNode {
  const { day } = useBusinessDay();
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [top, setTop] = useState<TopSearch[]>([]);
  const [failed, setFailed] = useState<FailedSearch[]>([]);
  const [popular, setPopular] = useState<PopularContent[]>([]);
  const [adoption, setAdoption] = useState<Adoption | null>(null);
  const [trends, setTrends] = useState<Trend[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: Overview }>(`/v1/search/analytics/metrics`, { token }),
      apiRequest<{ data: TopSearch[] }>(`/v1/search/analytics/top-queries`, { token }),
      apiRequest<{ data: FailedSearch[] }>(`/v1/search/analytics/failed`, { token }),
      apiRequest<{ data: PopularContent[] }>(`/v1/search/analytics/popular`, { token }),
      apiRequest<{ data: Adoption }>(`/v1/search/analytics/adoption`, { token }),
      apiRequest<{ data: Trend[] }>(`/v1/search/analytics/trends`, { token }),
    ])
      .then(([o, t, f, p, a, tr]) => {
        if (!alive) return;
        setOverview(o.data); setTop(t.data); setFailed(f.data);
        setPopular(p.data); setAdoption(a.data); setTrends(tr.data); setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="search-analytics-dashboard">
      <h1>{t('m39.analytics.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !overview || !adoption ? <p>{t('common.loading')}</p> : (
        <>
          <section aria-label="analytics-overview">
            <h2>{t('m39.analytics.overview')}</h2>
            <div className="kpi-grid">
              <div className="kpi-card"><span className="kpi-label">{t('m39.analytics.totalSearches')}</span><span className="kpi-value" aria-label="ov-total">{overview.totalSearches}</span></div>
              <div className="kpi-card"><span className="kpi-label">{t('m39.analytics.successful')}</span><span className="kpi-value" aria-label="ov-success">{overview.successfulSearches}</span></div>
              <div className="kpi-card"><span className="kpi-label">{t('m39.analytics.failed')}</span><span className="kpi-value" aria-label="ov-failed">{overview.failedSearches}</span></div>
              <div className="kpi-card"><span className="kpi-label">{t('m39.analytics.zeroResult')}</span><span className="kpi-value" aria-label="ov-zero">{overview.zeroResultSearches}</span></div>
              <div className="kpi-card"><span className="kpi-label">{t('m39.analytics.successRate')}</span><span className="kpi-value" aria-label="ov-rate">{number(overview.successRate)}%</span></div>
              <div className="kpi-card"><span className="kpi-label">{t('m39.analytics.avgExec')}</span><span className="kpi-value" aria-label="ov-avg">{overview.avgExecutionMs}</span></div>
              <div className="kpi-card"><span className="kpi-label">{t('m39.analytics.p95Target')}</span><span className="kpi-value" aria-label="ov-p95">{overview.p95TargetMs}</span></div>
            </div>
          </section>

          <section aria-label="analytics-adoption">
            <h2>{t('m39.analytics.adoption')}</h2>
            <dl>
              <dt>{t('m39.analytics.indexedItems')}</dt><dd aria-label="ad-indexed">{adoption.indexedItems}</dd>
              <dt>{t('m39.analytics.activeSearchers')}</dt><dd aria-label="ad-searchers">{adoption.activeSearchers}</dd>
              <dt>{t('m39.analytics.searchesPerSearcher')}</dt><dd aria-label="ad-ratio">{adoption.searchesPerSearcher}</dd>
            </dl>
          </section>

          <section aria-label="analytics-top">
            <h2>{t('m39.analytics.topQueries')}</h2>
            <DataTable
              testId="top-table"
              ariaLabel="top-table"
              rows={top}
              rowKey={(r) => r.query}
              emptyMessage={t('m39.analytics.noQueries')}
              columns={[
                { key: 'query', header: t('m39.common.query'), render: (r) => r.query },
                { key: 'count', header: t('m39.analytics.colCount'), render: (r) => r.count },
                { key: 'avgResults', header: t('m39.analytics.avgResults'), render: (r) => r.avgResults },
              ]}
            />
          </section>

          <section aria-label="analytics-failed">
            <h2>{t('m39.analytics.failedQueries')}</h2>
            <DataTable
              testId="failed-table"
              ariaLabel="failed-table"
              rows={failed}
              rowKey={(r) => `${r.query}:${r.status}`}
              emptyMessage={t('m39.analytics.noFailed')}
              columns={[
                { key: 'query', header: t('m39.common.query'), render: (r) => r.query },
                { key: 'status', header: t('m39.common.status'), render: (r) => r.status },
                { key: 'count', header: t('m39.analytics.colCount'), render: (r) => r.count },
              ]}
            />
          </section>

          <section aria-label="analytics-popular">
            <h2>{t('m39.analytics.popularContent')}</h2>
            <DataTable
              testId="popular-table"
              ariaLabel="popular-table"
              rows={popular}
              rowKey={(r) => r.searchIndexId}
              emptyMessage={t('m39.analytics.noClicks')}
              columns={[
                { key: 'title', header: t('m39.common.title'), render: (r) => r.title },
                { key: 'clicks', header: t('m39.analytics.clicks'), render: (r) => r.clicks },
              ]}
            />
          </section>

          <section aria-label="analytics-trends">
            <h2>{t('m39.analytics.dailyTrend')}</h2>
            <DataTable
              testId="trends-table"
              ariaLabel="trends-table"
              rows={trends}
              rowKey={(r) => r.day}
              emptyMessage={t('m39.analytics.noTrend')}
              columns={[
                { key: 'day', header: t('m39.analytics.colDay'), render: (r) => day(r.day) },
                { key: 'searches', header: t('m39.analytics.colSearches'), render: (r) => r.searches },
                { key: 'failures', header: t('m39.analytics.colFailures'), render: (r) => r.failures },
              ]}
            />
          </section>
        </>
      )}
    </main>
  );
}
